import { DEFAULT_LOCALE, localeSchema } from '@kidshub/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router';
import { sessionManager } from '../auth/session.js';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toInitial } from '../lib/turkish-case.js';
import { useResource } from '../lib/use-resource.js';
import { Icon, type IconName } from './kit.js';

const LOCALES = localeSchema.options;

interface AppShellProps {
  title: string;
  subtitle?: string;
  /** The page's own actions. On a phone they span the width under the title. */
  actions?: ReactNode;
  children: ReactNode;
}

interface Destination {
  to: string;
  icon: IconName;
  labelKey: string;
  /** The one action the tab bar holds out in ink, in the middle. */
  isAdd?: boolean;
  /** In the sidebar but not the phone's tab bar, which holds five. */
  isSidebarOnly?: boolean;
}

/**
 * Where a parent can go, in the order the tab bar shows it.
 *
 * Five on a phone, because a tab bar holds five comfortably and a parent
 * reaches all of them with a thumb. Adding a channel is the action, so it sits
 * in the middle in ink. Devices gave its tab to Watching (decided on the board
 * in `.docs/design/watch-analytics`): pairing is a once-in-a-while task, and
 * the overview links to it where a parent looks for a tablet anyway.
 */
const DESTINATIONS: Destination[] = [
  { to: '/', icon: 'panel', labelKey: 'nav.overview' },
  { to: '/library', icon: 'lib', labelKey: 'nav.library' },
  { to: '/library/import', icon: 'plus', labelKey: 'nav.addChannel', isAdd: true },
  { to: '/children', icon: 'child', labelKey: 'nav.children' },
  { to: '/watching', icon: 'chart', labelKey: 'nav.watching' },
  { to: '/devices', icon: 'tablet', labelKey: 'nav.devices', isSidebarOnly: true },
];

/**
 * The frame every signed-in page sits in (design direction A, "Keçe & Kâğıt").
 *
 * Phone first: a sticky top bar and a five-tab bar at the bottom, within reach
 * of a thumb. From 900px the tab bar gives way to an ink sidebar. The old shell
 * had only the sidebar, fixed at 226px, which on a phone took half the screen
 * and pushed every page sideways.
 */
export const AppShell = ({ title, subtitle, actions, children }: AppShellProps) => {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const family = useResource('children', () => apiClient.children.list());

  // With one child the tab is hers, by name; with several it is the list.
  const onlyChild =
    family.resource.status === 'ready' && family.resource.data.children.length === 1
      ? family.resource.data.children[0]
      : undefined;
  const label = (destination: Destination) =>
    destination.to === '/children' && onlyChild !== undefined
      ? onlyChild.child.name
      : t(destination.labelKey);

  const signOut = async () => {
    await sessionManager.signOut();
    void navigate('/login', { replace: true });
  };

  // Two locales, so the switch is a single button naming the one you are not in.
  const otherLocale = LOCALES.find((candidate) => candidate !== i18n.language) ?? DEFAULT_LOCALE;

  return (
    <div className="app">
      <aside className="side" aria-label={t('nav.main')}>
        <Link className="brand" to="/">
          <span className="patch" aria-hidden="true">
            K
          </span>
          {t('app.name')}
        </Link>

        {onlyChild === undefined ? null : (
          <Link className="side-child" to="/children">
            <span className="avatar" aria-hidden="true">
              {toInitial(onlyChild.child.name, locale)}
            </span>
            <span>
              <b>{onlyChild.child.name}</b>
              <span>{t(`ageBand.${onlyChild.child.ageBand}`)}</span>
            </span>
          </Link>
        )}

        <nav>
          {DESTINATIONS.map((destination) => (
            <NavLink
              key={destination.to}
              to={destination.to}
              end={destination.to === '/' || destination.to === '/library'}
              className="nav-link"
            >
              <Icon name={destination.icon} />
              {label(destination)}
            </NavLink>
          ))}
        </nav>

        <div className="side-foot">
          <button
            type="button"
            className="nav-link"
            onClick={() => void i18n.changeLanguage(otherLocale)}
          >
            {t(`language.${otherLocale}`)}
          </button>
          <button type="button" className="nav-link" onClick={() => void signOut()}>
            {t('auth.signOut')}
          </button>
        </div>
      </aside>

      <header className="topbar">
        <Link className="brand" to="/">
          <span className="patch" aria-hidden="true">
            K
          </span>
          {t('app.name')}
        </Link>
        <div className="topbar-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={() => void i18n.changeLanguage(otherLocale)}
          >
            {otherLocale.toUpperCase()}
          </button>
          <button type="button" className="icon-btn" onClick={() => void signOut()}>
            {t('auth.signOut')}
          </button>
        </div>
      </header>

      <main className="main">
        <div className="page-head">
          <div>
            <h1 className="t-h1">{title}</h1>
            {subtitle === undefined ? null : <p className="page-sub">{subtitle}</p>}
          </div>
          {actions === undefined ? null : <div className="page-actions">{actions}</div>}
        </div>
        {children}
      </main>

      <nav className="tabs" aria-label={t('nav.main')}>
        {DESTINATIONS.filter((destination) => !destination.isSidebarOnly).map((destination) => (
          <NavLink
            key={destination.to}
            to={destination.to}
            end={destination.to === '/' || destination.to === '/library'}
            className={destination.isAdd ? 'tab is-add' : 'tab'}
          >
            <span className="tab-ic">
              <Icon name={destination.icon} />
            </span>
            {label(destination)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
